import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const inputCls =
    "w-full rounded-md border border-[#1F293D] bg-[#0B0E14] px-3 py-2.5 text-sm text-slate-200 focus:border-[#00FF87] focus:outline-none";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("auth.reset_mismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail === "reset_invalid" ? t("auth.reset_invalid") : err?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex justify-center pt-16" data-testid="reset-password-page">
      <form onSubmit={submit} className="hud-card w-full max-w-sm rounded-xl p-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md border border-[#00FF87]/40 bg-[#00FF87]/15">
            <KeyRound className="h-4 w-4 text-[#00FF87]" />
          </span>
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-50">{t("auth.reset_title")}</h1>
        </div>

        {done ? (
          <>
            <p className="rounded-md border border-[#00FF87]/40 bg-[#00FF87]/10 px-3 py-2 text-xs text-[#00FF87]" data-testid="reset-success">
              {t("auth.reset_done")}
            </p>
            <Link to="/auth" data-testid="reset-goto-login" className="btn-volt mt-5 block text-center">
              {t("nav.sign_in")}
            </Link>
          </>
        ) : (
          <>
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("auth.reset_new")}</label>
            <input
              data-testid="reset-password-input"
              type="password"
              required
              minLength={8}
              className={`${inputCls} mb-4`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("auth.reset_confirm")}</label>
            <input
              data-testid="reset-password-confirm-input"
              type="password"
              required
              minLength={8}
              className={`${inputCls} mb-5`}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {(!token || error) && (
              <p className="mb-4 rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-400" data-testid="reset-error">
                {error || t("auth.reset_invalid")}
              </p>
            )}
            <button
              data-testid="reset-password-submit-button"
              type="submit"
              disabled={busy || !token}
              className="btn-volt w-full disabled:opacity-50"
            >
              {busy ? t("common.loading") : t("auth.reset_btn")}
            </button>
            <Link to="/auth" data-testid="reset-back-login" className="mt-4 block text-center text-xs text-slate-400 hover:text-[#00FF87]">
              {t("auth.back_login")}
            </Link>
          </>
        )}
      </form>
    </div>
  );
}
